<template>
  <div>
    <div id="overlay" v-if="fetchingData">
      <div class="spinner"></div>
    </div>

    <h3>Asbestos Contractor Registration</h3>
    <p>
      1. A signed and notarized copy of the Child Support Affidavit<br />
      2. A copy of IDPH Contractor License<br />
    </p>
    <base-modal title="Violation Details"
                ref="violationModal"
                @close="cancelViolation"
                @save="saveViolation">
      <div class="container-fluid">
        <base-input v-model="violation.enforcementActionType"
                    label="Enforcement Action Type"/>
        <base-date-picker v-model="violation.enforcementDate"
                          label="Enforcement Date"/>
        <base-input v-model="violation.currentDisposition"
                    label="Current Disposition" />
      </div>
    </base-modal>

    <exploded-fieldset title="Company Information" class="first">
      <base-input v-model="form.contractor.name"
                  maxlength="50"
                  label="Asbestos Contractor Company Name"
                  :readonly="isRenewal"
                  :validation="$v.form.contractor.name" />
      <hr />
      <v-address v-model="form.contractor.address"
                 :validation="$v.form.contractor.address"
                 labelPrefix="Contractor Address"
                 :show-unincorporated="false" />
      <hr />
      <div class="row">
        <div class="col-12 col-sm-6 col-lg-4">
          <base-input v-model="form.contractor.email"
                      label="Company Email"
                      :validation="$v.form.contractor.email" />
        </div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.contractor.phone"
                      label="Company Phone Number"
                      type="tel"
                      mask="(###)-###-####"
                      :validation="$v.form.contractor.phone" />
        </div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.contractor.fax"
                      type="tel"
                      mask="(###)-###-####"
                      label="Company Fax Number"
                      :validation="$v.form.contractor.fax" />
        </div>
      </div>
      <hr />
    </exploded-fieldset>
    <exploded-fieldset title="Company Contact Information">
      <div class="row">
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.contractor.contactFirstName"
                      label="Contact First Name"
                      :validation="$v.form.contractor.contactFirstName" />
        </div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.contractor.contactLastName"
                      label="Contact Last Name"
                      :validation="$v.form.contractor.contactLastName"/>
        </div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.contractor.contactPhone"
                      label="Contact Phone Number"
                      type="tel"
                      mask="(###)-###-####"
                      :validation="$v.form.contractor.contactPhone" />
        </div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.contractor.contactEmail"
                      label="Contact Email Address"
                      :validation="$v.form.contractor.contactEmail" />
        </div>
      </div>
      <v-address v-model="form.contractor.ownerAddress"
                 labelPrefix="Company Address"
                 :validation="$v.form.contractor.ownerAddress"
                 :show-unincorporated="false" />
    </exploded-fieldset>
    <exploded-fieldset title="Illinois Department of Public Health Information (IDPH)">
      <base-input v-model="form.contractor.contractorIDPH"
                  label="IDPH Asbestos Contractor License Number"
                  mask="###-####"
                  :validation="$v.form.contractor.contractorIDPH"/>
      <base-date-picker v-model="form.contractor.iDPHLicenseExpirationDate"
                        label="Expiration Date of IDPH Asbestos Contractor License"
                        :validation="$v.form.contractor.iDPHLicenseExpirationDate"/>
      <base-input v-model="form.contractor.numberofYearsLicensedbytheState"
                  v-numeric
                  maxlength="10"
                  label="Number of Years Licensed by the State"
                  :validation="$v.form.contractor.numberofYearsLicensedbytheState"/>
    </exploded-fieldset>
    <ExplodedFieldset title="Violations">
      <p>
        Please report any violations incurred in the past 2 years.
      </p>
      <table class="table table-striped table-hover w-100">
        <thead>
        <tr>
          <th>Enforcement Action Type</th>
          <th>Enforcement Date</th>
          <th>Current Disposition</th>
          <th>Actions</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="violation in form.violations" v-bind:key="violation.id">
          <td>
            {{violation.enforcementActionType}}
          </td>
          <td>
            {{violation.enforcementDate.toLocaleDateString()}}
          </td>
          <td>
            {{violation.currentDisposition}}
          </td>
          <td>
            <div class="btn-group" role="group" aria-label="Violation Edit and Delete Buttons">
              <button class="btn btn-sm btn-secondary" @click="editViolation(violation)">
                Edit
              </button>
              <button class="btn btn-sm btn-danger"
                      @click="deleteViolation(violation)">
                Delete
              </button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
      <button class="btn btn-small btn-primary" @click="addViolation">Add</button>
    </ExplodedFieldset>
    <exploded-fieldset title="Types of Asbestos Removal and Abatement">
      <div class="form-group">
        <p>
          What types of asbestos removal and abatement techniques
          are most frequently used by this contractor?
        </p>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 v-model="form.contractor.removalAbatementHeat"
                 value="heat"
                 id="abatement-type-heat">
          <label class="form-check-label" for="abatement-type-heat">
            Heat
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 v-model="form.contractor.removalAbatementGrossWetRemoval"
                 value="grosswetremove"
                 id="abatement-type-grosswetremove">
          <label class="form-check-label" for="abatement-type-grosswetremove">
            Gross Wet Removal
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 v-model="form.contractor.removalAbatementInfrared"
                 value="infrared"
                 id="abatement-type-infrared">
          <label class="form-check-label" for="abatement-type-infrared">
            Infrared
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 v-model="form.contractor.removalAbatementGlovebag"
                 value="glovebag"
                 id="abatement-type-glovebag">
          <label class="form-check-label" for="abatement-type-glovebag">
            Glovebag
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 v-model="form.contractor.removalAbatementSolvent"
                 value="solvent"
                 id="abatement-type-solvent">
          <label class="form-check-label" for="abatement-type-solvent">
            Solvent
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 v-model="form.contractor.removalAbatementOther"
                 value="solvent"
                 id="abatement-type-other">
          <label class="form-check-label"
                 for="abatement-type-other"
                 :class="{required: form.contractor.removalAbatementOther}">
            Other (Please describe)
          </label>
          <base-input v-model="form.contractor.abatementOther"
                      v-bind:readonly="!form.contractor.removalAbatementOther"
                      :validation="$v.form.contractor.abatementOther"/>
        </div>
      </div>
    </exploded-fieldset>
    <exploded-fieldset title="Attachments">
      <div class="row">
        <div class="col-6">
          <base-file-uploader v-model="form.files.idphContractorLicense"
                              :validation="$v.form.files.idphContractorLicense"
                              label="Copy of IDPH Contractor License"/>
        </div>
        <div class="col-6">
          <base-file-uploader v-model="form.files.childSupportAffidavit"
                              :validation="$v.form.files.childSupportAffidavit"
                              label="Child Support Affidavit"/>
        </div>
      </div>
    </exploded-fieldset>
    <exploded-fieldset title="Signature" class="last">
      <p>
        Any person who knowingly makes a false statement to Cook County Department of
        Environment and Sustainability is in direct violation of Cook County Ordinance Chapter 30
        and is subject to potential fines and penalties. By typing my name, I certify that I am in
        compliance with Cook County Ordinance Chapter 30 and all other applicable Cook County
        ordinances and statutes. All fees are non-refundable and credits will not be given.
      </p>
      <base-input v-model="form.submitterName"
                  label="Submitter's Name"
                  placeholder="Full Name"
                  :validation="$v.form.submitterName"/>
      <base-input v-model="form.title"
                  label="Submitter's Title"/>
      <base-date-picker v-model="form.signDate"
                        label="Date"
                        :readonly="false"
                        :validation="$v.form.signDate"/>
    </exploded-fieldset>
    <div class="row">
      <div class="col">
        <p class="float-right mt-3">
          This form cannot be submitted until all required fields are complete.
        </p>
      </div>
      <div class="w-100"></div>
      <div class="col">
        <!-- :disabled="$v.$invalid" -->
        <button type="button" class="btn btn-success float-right" @click="submitForm">&#10003; Submit</button>
      </div>
    </div>
  </div>
</template>

<script>
import ExplodedFieldset from '~/components/ExplodedFieldset';
import BaseInput from '~/components/BaseInput';
import VAddress from '~/components/VAddress';
import BaseSelect from '~/components/BaseSelect';
import BaseTextarea from '~/components/BaseTexarea';
import BaseDatePicker from '~/components/BaseDatePicker';
import BaseCheckbox from '~/components/BaseCheckbox';
import BaseFileUploader from '~/components/BaseFileUploader';
import YesNo from '~/components/YesNo';
import { ContractorRegistrationValidation } from '@/js/FormValidations';
import { ContractorApplication, Violation, Contractor } from '~/js/FormObjects';
import { getContractorRegistration, errorNotification } from '~/js/FormFunctions';
import BaseModal from '@/components/BaseModal';
import cloneDeep from 'lodash/fp/cloneDeep';

export default {
  name: 'ContractorApplication',
  props: {
    contractorId: String,
    isRenewal: Boolean,
  },
  components: {
    BaseModal,
    BaseDatePicker,
    BaseInput,
    VAddress,
    ExplodedFieldset,
    BaseSelect,
    BaseTextarea,
    BaseCheckbox,
    BaseFileUploader,
    YesNo,
  },
  data() {
    return {
      fetchingData: false,
      // current violation being edited
      violation: new Violation(),
      form: new ContractorApplication(new Contractor('asbestos')),
    };
  },
  mounted() {
    const self = this;
    if (this.$store.getters.temporaryForm) {
      this.$set(this, 'form', this.$store.getters.temporaryForm);
      this.$store.commit('clearForm');
    }

    if (this.contractorId && !this.form.id) {
      this.fetchingData = true;
      getContractorRegistration(this.contractorId)
        .then((data) => {
          const form = new ContractorApplication(data);
          self.$set(self, 'form', form);
          self.fetchingData = false;
        })
        .catch((error) => {
          errorNotification('Error getting contractor registration: ', error);
          self.fetchingData = false;
          self.$notify('Error getting contractor record', 'error');
        });
    }
  },
  methods: {
    validateForm() {
      this.$v.$touch();
    },
    submitForm() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        this.$notify('Registration form is invalid.', 'error');
        return;
      }
      const self = this;

      const formData = new FormData();
      Object.keys(this.form.files).forEach((key) => {
        if (self.form.files[key]) {
          formData.append(key, self.form.files[key]);
        }
      });

      formData.append('form', JSON.stringify(this.form));
      this.fetchingData = true;

      this.$http.post('/api/contractor/saveRegistration', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then((response) => {
          self.fetchingData = false;
          self.$router.push({
            name: 'Checkout',
            query: {
              contractorRegId: response.data,
            },
          });
        })
        .catch((error) => {
          self.fetchingData = false;
          errorNotification('Error saving contractor registration: ', error);
        });
    },
    addViolation() {
      this.$refs.violationModal.start();
    },
    cancelViolation() {
      Object.keys(this.violation).forEach((k) => {
        this.violation[k] = null;
      });
    },
    saveViolation() {
      console.log('violation id: ', this.violation.id);
      if (!this.violation.id) {
        let id = 0;
        if (this.form.violations.length > 0) {
          id = Math.max(this.form.violations.map(v => v.id));
        }
        this.violation.id = 1 + id;
      } else {
        this.form.violations = this.form.violations.filter(v => v.id !== this.violation.id);
      }
      this.form.violations.push(cloneDeep(this.violation));
      // set all the properties to null
      Object.keys(this.violation).forEach((k) => {
        this.violation[k] = null;
      });
    },
    editViolation(violation) {
      this.violation = cloneDeep(violation);
      this.$refs.violationModal.start();
    },
    deleteViolation(violation) {
      const vIndex = this.form.violations.indexOf(violation);
      this.form.violations.splice(vIndex, 1);
    },
  },
  validations: {
    form: new ContractorRegistrationValidation(),
  },
};
</script>
